import { useRef, useState } from 'react';
import { useReglages } from '../api/reglages.jsx';
import { iaApi } from '../api/resources';
import { prepareLeJustificatif } from '../lib/justificatif';
import { litLesTextes, litTexteTicket } from '../lib/lecture-ticket';
import { texteDeLaPhoto, texteDuJustificatif } from '../lib/ocr-ticket';
import { CLE_PACK_IA, packIaActif } from '../lib/pack-ia';
import { C } from '../theme/palette';

/** Types que le serveur accepte pour la lecture d'un ticket. */
export const TYPES_TICKET = 'image/jpeg,image/png,image/webp,application/pdf';

/** Taille maximale d'un ticket envoyé à la lecture, en octets. */
const OCTETS_MAX = 10 * 1024 * 1024;

const EN_COURS = 'en cours';

/** Lit un fichier en adresse de données, sans le réduire. */
const enAdresse = (fichier) =>
  new Promise((resout, rejette) => {
    const lecteur = new FileReader();
    lecteur.onload = () => resout(String(lecteur.result || ''));
    lecteur.onerror = () => rejette(new Error('Le fichier n’a pas pu être lu.'));
    lecteur.readAsDataURL(fichier);
  });

/**
 * Sans montant trouvé, la lecture d'une photo n'est pas sûre : le libellé et le
 * fournisseur se remplissaient de signes sans suite (« EN gla) of [ERY… »), et
 * la catégorie suivait ces signes. Ils restent vides, à compléter ; la date lue
 * reste.
 *
 * @param {object} lu lecture du ticket
 * @returns {object} la lecture, sans les noms d'une lecture ratée
 */
function sansCharabia(lu) {
  if (lu.montantTtc != null) return lu;
  return { ...lu, fournisseur: null, adresse: null, libelle: null, categorie: null };
}

/**
 * La lecture d'un ticket, pour la lancer d'où l'on veut : le bouton « Lire le
 * ticket », ou directement « Scanner » et « Pièce jointe » des dépenses — le
 * patron scannait son ticket et rien ne se remplissait.
 *
 * <p>Avec le Pack IA, le ticket part à sa lecture. Sans lui, ou quand il ne
 * répond pas, le ticket est lu dans le téléphone (lib/ocr-ticket.js) : la
 * dépense prise en photo ne reste plus vide. La pièce est réduite avant tout,
 * comme tout justificatif de dépense : ce qui est lu est ce qui sera joint.
 * Quand la réduction ne suffit pas — un PDF trop lourd —, le ticket est tout
 * de même lu, et l'écran dit que la pièce n'a pas pu être jointe.
 *
 * @param {(lecture: object, piece: object|null) => void} onLecture reçoit ce qui a été lu
 *   et la pièce prête à joindre, ou `null` si elle n'a pas pu l'être
 * @returns {{actif: boolean, iaActif: boolean, lit: (fichier: File) => Promise<void>,
 *   etat: string, enCours: boolean}} la lecture ; `iaActif` dit si le Pack IA lit
 */
export function useLectureTicket(onLecture) {
  const { reglageJson, societe } = useReglages();
  // La société qui paie n'est jamais le commerce ; ses taux de TVA sont ceux
  // du pays de son adresse.
  const options = { nousMemes: [societe?.nom].filter(Boolean), pays: societe?.pays };
  const iaActif = packIaActif(reglageJson(CLE_PACK_IA, null));
  const [etat, setEtat] = useState('');

  const lit = async (fichier) => {
    if (!fichier) return;
    if (fichier.size > OCTETS_MAX) {
      setEtat('Ce fichier dépasse les 10 Mo admis pour la lecture.');
      return;
    }
    setEtat(EN_COURS);
    let piece = null;
    let avertissement = '';
    let contenu;
    try {
      piece = await prepareLeJustificatif(fichier);
      contenu = piece.piece;
    } catch (e) {
      // Trop lourd pour être joint, pas pour être lu : on lit quand même.
      avertissement = e?.message || '';
      contenu = await enAdresse(fichier);
    }
    const fin = avertissement ? avertissement + ' Le ticket a été lu sans être joint.' : '';
    if (iaActif) {
      try {
        const lecture = await iaApi.lectureDepense({
          contenu,
          typeMedia: (piece && piece.pieceMime) || fichier.type || '',
          nomFichier: fichier.name || 'ticket',
        });
        onLecture(lecture, piece);
        setEtat(fin);
        return;
      } catch {
        // Le Pack IA ne répond pas (serveur sans clé, réseau) : le ticket se
        // lit dans le téléphone plutôt que de rester vide.
      }
    }
    try {
      const pdf = fichier.type === 'application/pdf' || /\.pdf$/i.test(fichier.name || '');
      // Une photo est lue plusieurs fois, ce qui se recoupe est gardé ; prise
      // couchée ou à l'envers, elle est relue tournée tant que le montant
      // n'est pas trouvé.
      const texte = pdf
        ? await texteDuJustificatif(fichier)
        : await texteDeLaPhoto(fichier, (t) => litLesTextes(t, options).montantTtc != null);
      const lu = sansCharabia(litLesTextes(texte, options));
      onLecture(lu, piece);
      setEtat(
        lu.montantTtc == null
          ? 'Le ticket est difficile à lire : reprends la photo bien à plat, droite et éclairée, ou complète le montant toi-même.'
          : fin,
      );
    } catch {
      onLecture(litTexteTicket(''), piece);
      setEtat('Le ticket n’a pas pu être lu : complète le montant et la date toi-même.');
    }
  };
  return { actif: true, iaActif, lit, etat, enCours: etat === EN_COURS };
}

/**
 * « 📷 Lire le ticket » : la photo est lue, le formulaire revient rempli.
 *
 * @param {object} props propriétés du composant
 * @param {(lecture: object, piece: object|null) => void} props.onLecture reçoit ce qui a été lu
 *   et la pièce prête à joindre, ou `null` si elle n'a pas pu l'être
 * @param {string} [props.color] couleur d'accent
 * @returns {import('react').ReactElement} le bouton
 */
export function LectureTicket({ onLecture, color = C.purple }) {
  const { iaActif, lit, etat, enCours } = useLectureTicket(onLecture);
  const entree = useRef(null);
  return (
    <div style={{ marginBottom: 10 }}>
      <button
        type="button"
        onClick={() => entree.current && entree.current.click()}
        disabled={enCours}
        aria-label="Lire le ticket"
        style={{
          width: '100%',
          background: enCours ? C.border : color,
          color: '#fff',
          border: 'none',
          borderRadius: 9,
          padding: '11px',
          fontSize: 10.5,
          fontWeight: 700,
          cursor: enCours ? 'wait' : 'pointer',
        }}
      >
        {enCours
          ? '📷 Lecture du ticket…'
          : iaActif
            ? '📷 Lire le ticket (Pack IA)'
            : '📷 Lire le ticket'}
      </button>
      <input
        ref={entree}
        type="file"
        accept={TYPES_TICKET}
        aria-label="Ticket à lire"
        style={{ display: 'none' }}
        onChange={(e) => {
          const fichier = e.target.files && e.target.files[0];
          e.target.value = '';
          lit(fichier);
        }}
      />
      {etat && !enCours && (
        <div role="alert" style={{ fontSize: 9.5, color: C.red, marginTop: 5, lineHeight: 1.5 }}>
          {etat}
        </div>
      )}
    </div>
  );
}
